import { Slideshow } from '@theme/slideshow';

/**
 * Card slider component that extends the theme's core Slideshow class.
 * Transforms arbitrary nested theme blocks into an interactive slideshow
 * while utilizing the theme's battle-tested Scroller, drag physics,
 * autoplay engine, and accessibility architecture.
 *
 * @extends {Slideshow}
 */
export class CardSliderComponent extends Slideshow {
  async connectedCallback() {
    this.#setupDynamicSlides();
    this.#setupDynamicDots();

    await super.connectedCallback();
  }

  updatedCallback() {
    this.#setupDynamicSlides();
    this.#setupDynamicDots();

    super.updatedCallback();
  }

  /**
   * Tags dynamic child blocks inside scroller with ref="slides[]" and slide-id
   * so that the core Slideshow class can discover and register them into this.refs.slides.
   */
  #setupDynamicSlides() {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    const slides = Array.from(scroller.children).filter(
      (el) => !el.matches('slideshow-arrows, slideshow-controls, [data-placeholder]')
    );

    slides.forEach((slide, i) => {
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('slide-id', `card-slide-${i}`);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${i + 1} of ${slides.length}`);
      slide.classList.add('card-slider__slide-item');
    });
  }

  /**
   * Dynamically renders pagination dots with ref="dots[]" so that the core Slideshow class
   * controls their active states (aria-selected="true/false") and routes on:click="/select/${i}".
   */
  #setupDynamicDots() {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    const slides = Array.from(scroller.children).filter(
      (el) => !el.matches('slideshow-arrows, slideshow-controls, [data-placeholder]')
    );

    let controls = /** @type {HTMLElement | null} */ (this.querySelector('slideshow-controls'));
    if (!controls) {
      controls = /** @type {HTMLElement} */ (document.createElement('slideshow-controls'));
      controls.className = 'card-slider__controls';
      this.appendChild(controls);
    }

    if (slides.length <= 1) {
      controls.hidden = true;
      controls.style.display = 'none';
      return;
    }
    controls.hidden = false;
    controls.style.display = 'flex';

    let dotsList = controls.querySelector('.slideshow-controls__dots');
    if (!dotsList) {
      dotsList = document.createElement('ol');
      dotsList.className = 'slideshow-controls__dots';
      dotsList.setAttribute('role', 'tablist');
      dotsList.setAttribute('aria-label', 'Pagination');
      controls.appendChild(dotsList);
    }

    dotsList.innerHTML = '';
    slides.forEach((_, i) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'presentation');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'card-slider__dot';
      btn.setAttribute('ref', 'dots[]');
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Slide ${i + 1} of ${slides.length}`);
      btn.setAttribute('on:click', `/select/${i}`);
      if (i === 0) btn.setAttribute('aria-selected', 'true');

      li.appendChild(btn);
      dotsList.appendChild(li);
    });
  }

  /**
   * Resets autoplay countdown when user navigates manually.
   * @param {Event} [event]
   * @param {Object} [options]
   */
  next(event, options) {
    super.next(event, options);
    this.resume();
  }

  /**
   * Resets autoplay countdown when user navigates manually.
   * @param {Event} [event]
   * @param {Object} [options]
   */
  previous(event, options) {
    super.previous(event, options);
    this.resume();
  }

  /**
   * Resets autoplay countdown when user clicks a dot.
   * @param {number|string|{id: string}} input
   * @param {Event} [event]
   * @param {Object} [options]
   */
  async select(input, event, options) {
    await super.select(input, event, options);
    if (event) this.resume();
  }
}

if (!customElements.get('card-slider-component')) {
  customElements.define('card-slider-component', CardSliderComponent);
}
